import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Same conditions under which Home.css / Outfits.css use the mobile tooltip styles
const MOBILE_TOOLTIP_QUERY = '(max-width: 768px), (hover: none) and (pointer: coarse)';
const EDGE_MARGIN = 8;
const GAP = 12;

// On mobile the tooltip card is wider than the space around a hotspot, so it is
// rendered into <body> and placed with position: fixed next to its own dot:
// above it when there is room, otherwise below, and nudged sideways to stay on
// screen. (Inside the hotspot, its transform would make "fixed" relative to the
// dot instead of the screen.) Desktop keeps the plain CSS positioning.
const HotspotTooltipLayer: React.FC<{ children: React.ReactElement<any> }> = ({ children }) => {
  const isMobile = window.matchMedia(MOBILE_TOOLTIP_QUERY).matches;
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; arrowLeft: number; below: boolean } | null>(null);

  useLayoutEffect(() => {
    if (!isMobile) return;

    const place = () => {
      const hotspot = anchorRef.current?.parentElement;
      const tooltip = tooltipRef.current;
      if (!hotspot || !tooltip) return;

      const dot = (hotspot.querySelector('.hotspot-dot') as HTMLElement) || hotspot;
      const rect = dot.getBoundingClientRect();
      const width = tooltip.offsetWidth;
      const height = tooltip.offsetHeight;

      const centeredLeft = rect.left + rect.width / 2 - width / 2;
      const left = Math.min(Math.max(centeredLeft, EDGE_MARGIN), window.innerWidth - width - EDGE_MARGIN);
      // Room above is measured from the bottom of the header logo (it hangs
      // below the header), not from the top of the screen
      const logo = document.querySelector('.logo-layer .logo img');
      const topLimit = (logo ? logo.getBoundingClientRect().bottom : 0) + EDGE_MARGIN;
      const above = rect.top - GAP - height;
      const below = above < topLimit;
      const top = below ? rect.bottom + GAP : above;
      // Arrow sits over the dot, kept clear of the card's rounded corners
      const arrowLeft = Math.min(Math.max(rect.left + rect.width / 2 - left, 20), width - 20);

      setPosition({ top, left, arrowLeft, below });
    };

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [isMobile]);

  if (!isMobile) return children;

  return (
    <>
      <span ref={anchorRef} style={{ display: 'none' }} />
      {createPortal(
        React.cloneElement(children, {
          ref: tooltipRef,
          className: `${children.props.className || ''} hotspot-tooltip--floating${position?.below ? ' hotspot-tooltip--below' : ''}`,
          // Passed as CSS variables: older mobile rules set top/left with !important,
          // which plain inline styles can't beat
          style: {
            ...children.props.style,
            '--tooltip-top': `${position?.top ?? 0}px`,
            '--tooltip-left': `${position?.left ?? 0}px`,
            '--tooltip-arrow-left': `${position?.arrowLeft ?? 0}px`,
            visibility: position ? 'visible' : 'hidden',
          },
        }),
        document.body
      )}
    </>
  );
};

export default HotspotTooltipLayer;
